import { useState } from "react";
import { Tabs } from "@/components/ui/tabs";
import { BooksManagement } from "./BooksManagement";
import { MembersManagement } from "./MembersManagement";
import { BorrowReturnSystem } from "./BorrowReturnSystem";
import { ActivityLog } from "./ActivityLog";
import { LibraryReports } from "./LibraryReports";
import { Book, Users, RefreshCw, FileText, BarChart3 } from "lucide-react";

/**
 * LibraryContent - Shared library management content
 * 
 * This component contains all the library tabs and content.
 * It's used by both AdminLibraryManagement and TeacherLibraryManagement
 * to avoid code duplication. Each parent provides its own layout (sidebar/header).
 */
export const LibraryContent = () => {
  const [activeTab, setActiveTab] = useState("books");

  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .stable-gutter { scrollbar-gutter: stable; }
      `}} />
      
      <div className="mb-4 sm:mb-6 w-full max-w-full min-h-0 flex flex-col">
        <div className="mb-6 flex-shrink-0">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
            Library Management
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Manage books, members, borrowing, and library operations
          </p>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab} 
          className="w-full flex flex-col min-h-0 flex-1 overflow-hidden"
        >
          <div className="w-full overflow-x-auto scrollbar-hide flex-shrink-0 -mx-2 px-2 sm:mx-0 sm:px-0">
            <div className="flex w-full gap-1.5 p-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <button
                onClick={() => setActiveTab("books")}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === "books"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Book className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Books</span>
              </button>
              
              <button
                onClick={() => setActiveTab("members")}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === "members"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Users className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Members</span>
              </button>
              
              <button
                onClick={() => setActiveTab("borrow-return")}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === "borrow-return"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <RefreshCw className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Borrow/Return</span>
              </button>
              
              <button
                onClick={() => setActiveTab("activity")}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === "activity"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Activity</span>
              </button>
              
              <button
                onClick={() => setActiveTab("reports")}
                className={`flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === "reports"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <BarChart3 className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Reports</span>
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 mt-6 w-full overflow-hidden">
            {activeTab === "books" && (
              <div className="h-full w-full overflow-hidden">
                <BooksManagement />
              </div>
            )}
            
            {activeTab === "members" && (
              <div className="h-full w-full overflow-hidden">
                <MembersManagement />
              </div>
            )}
            
            {activeTab === "borrow-return" && (
              <div className="h-full w-full overflow-hidden">
                <BorrowReturnSystem />
              </div>
            )}
            
            {activeTab === "activity" && (
              <div className="h-full w-full overflow-hidden">
                <ActivityLog />
              </div>
            )}
            
            {activeTab === "reports" && (
              <div className="h-full w-full overflow-hidden">
                <LibraryReports />
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </>
  );
};

export default LibraryContent;
